import { useMemo } from 'react';
import { useSetRecoilState } from 'recoil';
import useSWRImmutable from 'swr/immutable';

import { FlightServicesModel } from '@/lib/models/flight-service/types';
import { transformCarrierDictionary } from '@/lib/transformers/carrier';
import { transformFlightServices } from '@/lib/transformers/flight-service';
import { transformLocationDictionary } from '@/lib/transformers/location';
import { transformVehicleDictionary } from '@/lib/transformers/vehicle';
import { carriersState } from '@/state/carriers';
import { locationsState } from '@/state/locations';
import { vehiclesState } from '@/state/vehicles';
import { ServicesApiParams } from '@/types';
import { ServicesApiInterface } from '@/types/api-types';
import { AsyncDataInterface } from '@/types/context-types';
import { request } from '@/utils/api/request';
import { getFlightServicesUrlWithParams } from '@/utils/helpers/api-urls';

interface FlightServicesState extends AsyncDataInterface {
  flightServices: FlightServicesModel[];
}

const ERROR_MESSAGE = 'Error fetching flight services';

export const useFlightServices = (params: ServicesApiParams): FlightServicesState => {
  const setCarriers = useSetRecoilState(carriersState);
  const setVehicles = useSetRecoilState(vehiclesState);
  const setLocations = useSetRecoilState(locationsState);

  const { isLoading, error, data } = useSWRImmutable<ServicesApiInterface>(
    getFlightServicesUrlWithParams(params),
    request,
    {
      onSuccess: (response) => {
        // Merge shared response data with existing recoil state
        const { carriers, vehicles, locations } = response;
        setCarriers((prev) => ({ ...prev, ...transformCarrierDictionary(carriers) }));
        setVehicles((prev) => ({ ...prev, ...transformVehicleDictionary(vehicles) }));
        setLocations((prev) => ({ ...prev, ...transformLocationDictionary(locations) }));
      },
    },
  );

  const errorMessage = error ? ERROR_MESSAGE : undefined;

  const transformedData = useMemo(
    () => data?.services.map((service) => transformFlightServices(service, data?.vehicles, data?.carriers)) || [],
    [data],
  );

  return {
    flightServices: transformedData,
    isLoading,
    error: errorMessage,
  };
};
